import { useState } from 'react'

import { Box, Card, Section, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import {
  EngagementSettingEmployeePulseFragment,
  UpdateEmployeePulseConfigDocument,
} from 'generated/resources/graphql'
import { undefined } from 'zod'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { FrequencyStep } from 'modules/engagement/components/Settings/FrequencyStep'
import { MessageStep } from 'modules/engagement/components/Settings/MessageStep'
import { PulseConfiguration } from 'modules/engagement/lib/pulse'

interface Props {
  settings: EngagementSettingEmployeePulseFragment
  advancedConfigurationEnabled: boolean
  hasEngagement: boolean
  canRequestUpsell: boolean
}

export function EmployeePulseConfig({
  settings,
  advancedConfigurationEnabled,
  hasEngagement,
  canRequestUpsell,
}: Props) {
  const [config, setConfig] = useState<PulseConfiguration>({
    frequency: settings.frequency || 'daily',
    days: settings.days || [],
    questionType: settings.questionType || 'daily_question',
    question: settings.question || '',
  })
  const [employeePulseEnabled, setEmployeePulseEnabled] = useState<boolean>(
    settings.employeePulseEnabled
  )

  const [updateEmployeePulseConfig, { loading: submitting }] = useMutation(
    UpdateEmployeePulseConfigDocument,
    {
      extractorFn: (data) => data?.engagementMutations?.updateEngagementSetting,
    }
  )

  const handleSubmit = async () => {
    const result = await updateEmployeePulseConfig({
      variables: {
        id: settings.id,
        days: config.days,
        employeePulseEnabled: employeePulseEnabled,
        frequency: config.frequency,
        question: config.question,
        questionType: config.questionType,
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }

    return handled
  }

  const frequencyValid = config.frequency === 'daily' || config.days.length > 0
  const messageValid = config.questionType !== 'custom' || config.question.length > 0
  const canSave = !employeePulseEnabled || (frequencyValid && messageValid)

  return (
    <>
      <Section.TwoThirds
        illustration="eNPS"
        title={i18n.t('engagement.settings.employee_pulse.title')}
        description={i18n.t('engagement.settings.employee_pulse.description')}
      >
        <Box paddingLeft="s80" flexDirection="row" justifyContent="flexEnd">
          <Box maxWidth="s600" width="s600" gap="s32">
            <Card>
              <Card.Title
                action={
                  <TogglerCheckbox
                    value={settings.employeePulseEnabled}
                    onChange={setEmployeePulseEnabled}
                    disabled={submitting}
                  />
                }
              >
                {i18n.t('engagement.settings.employee_pulse.action')}
              </Card.Title>
              <Card.Content>
                <Text size="300" color="grey1000">
                  {i18n.t('engagement.settings.employee_pulse.box_description')}
                </Text>
              </Card.Content>
            </Card>

            <Box width="full" flexDirection="row" justifyContent="flexStart">
              <Box width="fit-content">
                <F0Button
                  label={i18n.t('engagement.settings.employee_pulse.save')}
                  onClick={handleSubmit}
                  disabled={!canSave}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Section.TwoThirds>
      {employeePulseEnabled && advancedConfigurationEnabled && (
        <Section.TwoThirds
          illustration="bell"
          title={i18n.t('engagement.pulse.activation.frequency.header')}
          description={i18n.t('engagement.pulse.activation.frequency.intro')}
        >
          <Box paddingLeft="s80" gap="s24" flexDirection="column" alignItems="flexEnd">
            <Box maxWidth="s600" gap="s64">
              <FrequencyStep data={config} completed loading={false} onDataChanged={setConfig} />
            </Box>
          </Box>
        </Section.TwoThirds>
      )}
      {employeePulseEnabled && advancedConfigurationEnabled && (
        <Section.TwoThirds
          illustration="document-edit"
          title={i18n.t('engagement.pulse.activation.message.header')}
          description={i18n.t('engagement.settings.employee_pulse.description')}
        >
          <Box paddingLeft="s80" gap="s24" flexDirection="column" alignItems="flexEnd">
            <Box maxWidth="s600" gap="s64">
              <MessageStep
                data={config}
                completed
                loading={false}
                onDataChanged={setConfig}
                hasEngagement={hasEngagement}
                canRequestUpsell={canRequestUpsell}
              />
            </Box>
          </Box>
        </Section.TwoThirds>
      )}
    </>
  )
}
